// A bottom-centre "skill level up" banner: the skill's pixel icon + name + new level.
// Slides up, holds, then fades out. Used by the mining/pickaxe progression.

import { injectTheme } from "./theme.js";
import { drawItemIcon } from "../items/itemArt.js";

const STYLE_ID = "ghostland-skillnotice-style";

const CSS = `
#gl-skillnotice {
  position: fixed; left: 50%; bottom: 92px; transform: translate(-50%, 16px); z-index: 33;
  display: none; align-items: center; gap: 12px; padding: 10px 16px 10px 12px;
  background: var(--gl-panel); border: 1px solid var(--gl-border-focus); border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,.6), 0 0 0 1px rgba(124,58,237,.2);
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text);
  opacity: 0; transition: opacity .3s ease, transform .3s ease;
}
#gl-skillnotice.show { display: flex; opacity: 1; transform: translate(-50%, 0); }
#gl-skillnotice .gl-sn-icon {
  width: 40px; height: 40px; image-rendering: pixelated;
  background: var(--gl-panel-2); border: 1px solid var(--gl-border); border-radius: 8px; padding: 3px;
}
#gl-skillnotice .gl-sn-skill { font-size: 10px; letter-spacing: .16em; text-transform: uppercase; color: var(--gl-muted); }
#gl-skillnotice .gl-sn-level { font-size: 15px; font-weight: 700; color: var(--gl-selected); margin-top: 1px; }
`;

let el: HTMLDivElement | null = null;
let iconWrap: HTMLDivElement | null = null;
let skillEl: HTMLDivElement | null = null;
let levelEl: HTMLDivElement | null = null;
let hideTimer = 0;

/** Each skill maps to an item-icon id (its tool) for the badge. */
const SKILL_ICON: Record<string, string> = {
  mining: "pickaxe", skinning: "skinning_knife", cooking: "hog_burger", fishing: "fishing_rod",
  strength: "sword", dex: "skinning_knife", knowledge: "ghost_scroll", agility: "black_shoes",
};
const SKILL_NAME: Record<string, string> = {
  mining: "Mining", skinning: "Skinning", cooking: "Cooking", fishing: "Fishing",
  strength: "Strength", dex: "Dexterity", knowledge: "Knowledge", agility: "Agility",
};

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (el) return;
  el = document.createElement("div");
  el.id = "gl-skillnotice";
  iconWrap = document.createElement("div"); iconWrap.className = "gl-sn-icon";
  const text = document.createElement("div");
  skillEl = document.createElement("div"); skillEl.className = "gl-sn-skill";
  levelEl = document.createElement("div"); levelEl.className = "gl-sn-level";
  text.append(skillEl, levelEl);
  el.append(iconWrap, text);
  document.body.appendChild(el);
}

export function showSkillLevelUp(skill: string, level: number): void {
  ensure();
  // refresh the pixel icon (a fresh 16x16 canvas scaled up by the icon box)
  iconWrap!.replaceChildren();
  const icon = drawItemIcon(SKILL_ICON[skill] ?? "pickaxe");
  icon.style.cssText = "width:100%;height:100%;image-rendering:pixelated;";
  iconWrap!.appendChild(icon);
  skillEl!.textContent = `${SKILL_NAME[skill] ?? skill} — Level Up`;
  levelEl!.textContent = `Level ${level}`;
  el!.classList.add("show");
  window.clearTimeout(hideTimer);
  hideTimer = window.setTimeout(() => el!.classList.remove("show"), 3200);
}
